import React from 'react';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import { v4 as uuIdv4 } from 'uuid';
import {
  TextField,
  Button,
  Grid,
  Box,
  CircularProgress,
  Typography,
  MenuItem,
  IconButton,
  Tooltip,
  Autocomplete,
} from '@mui/material';
import { Refresh, ContentCopy } from '@mui/icons-material';
import SaveIcon from '@mui/icons-material/Save';
import { Notifier } from '../../../system/services/notificationManager';
import { useAuthorizer } from '../../../auth/AuthorizationContext';
import {
  useCreatePlmContractMutation,
  useUpdatePlmContractMutation,
  usePlmProgramOptionsQuery,
  usePlmPartnerOptionsQuery,
  usePlmContractUserOptionsQuery,
  // usePropertyOptionsQuery,
  EnumPlmContractBaselineMethod,
  EnumPlmContractScopeType,
  EnumPlmContractStatus,
} from '../../../../types/generated-types';
import { useNavigate } from 'react-router-dom';
import BorderedSection from '../../shared/borderedSection';
import { useProperties } from '../../../system/EntityManager';

const validationSchema = Yup.object().shape({
  name: Yup.string().required('Name is required'),
  contractId: Yup.string().required('Contract ID is required'),
  programId: Yup.string().required('Program is required'),
  propertyIds: Yup.array()
    .of(Yup.string())
    .min(1, 'At least one property is required'),
  scopeType: Yup.mixed<EnumPlmContractScopeType>()
    .oneOf(Object.values(EnumPlmContractScopeType))
    .required('Scope is required'),
  meterIds: Yup.array()
    .of(Yup.string())
    .when('scopeType', {
      is: EnumPlmContractScopeType.Meter,
      then: (schema) =>
        schema.min(1, 'At least one meter id is required for meter scope'),
      otherwise: (schema) => schema,
    }),
  partnerId: Yup.string().required('Partner is required'),
  dispatcherIds: Yup.array()
    .of(Yup.string())
    .min(1, 'At least one dispatcher is required'),
  minimumReductionKW: Yup.number()
    .required('Minimum reduction is required')
    .positive('Minimum reduction must be positive'),
  maximumEventsPerSeason: Yup.number()
    .required('Maximum events per season is required')
    .positive('Maximum events must be positive')
    .integer('Maximum events must be a whole number'),
  baselineMethod: Yup.string().required('Baseline method is required'),
  incentiveDescription: Yup.string().nullable(),
  penaltyForNonCompliance: Yup.number()
    .nullable()
    .min(0, 'Penalty must be non-negative'),
  status: Yup.string().required('Status is required'),
  notificationEmail: Yup.string()
    .required('Notification email is required')
    .email('Invalid email format'),
});

interface FormValues {
  name: string;
  contractId: string;
  programId: string;
  propertyIds: string[];
  scopeType: EnumPlmContractScopeType;
  meterIds: string[];
  partnerId: string;
  dispatcherIds: string[];
  minimumReductionKW: number;
  maximumEventsPerSeason: number;
  baselineMethod: EnumPlmContractBaselineMethod;
  incentiveDescription?: string;
  penaltyForNonCompliance?: number;
  status: EnumPlmContractStatus;
  notificationEmail: string;
}

const calculatePlmContractFormValues = (plmContract?: any): FormValues => ({
  name: plmContract?.name || '',
  contractId: plmContract?.contractId || '',
  programId: plmContract?.programId || '',
  propertyIds:
    plmContract?.propertyIds?.filter(
      (id: string | null): id is string => id !== null,
    ) || [],
  scopeType:
    (plmContract?.scopeType as EnumPlmContractScopeType) ||
    EnumPlmContractScopeType.Property,
  meterIds:
    plmContract?.meterIds?.filter(
      (id: string | null): id is string => id !== null,
    ) || [],
  partnerId: plmContract?.partnerId || '',
  dispatcherIds:
    plmContract?.dispatcherIds?.filter(
      (id: string | null): id is string => id !== null,
    ) || [],
  minimumReductionKW: plmContract?.minimumReductionKW || 0,
  maximumEventsPerSeason: plmContract?.maximumEventsPerSeason || 0,
  baselineMethod:
    plmContract?.baselineMethod ||
    EnumPlmContractBaselineMethod.Average10PriorWeekdaysBaseline,
  incentiveDescription: plmContract?.incentiveDescription || undefined,
  penaltyForNonCompliance: plmContract?.penaltyForNonCompliance || undefined,
  status: plmContract?.status || EnumPlmContractStatus.Draft,
  notificationEmail:
    plmContract?.notificationEmail || `${uuIdv4()}@plm.embue.com`,
});

interface PlmContractDetailFormProps {
  plmContract?: any;
  loading?: boolean;
  error?: Error;
}

export const PlmContractDetailForm: React.FC<PlmContractDetailFormProps> = ({
  plmContract,
  loading,
  error,
}) => {
  const navigate = useNavigate();
  const { can } = useAuthorizer();
  const [createPlmContract] = useCreatePlmContractMutation();
  const [updatePlmContract] = useUpdatePlmContractMutation();

  const { data: programOptionsData, loading: programOptionsLoading } =
    usePlmProgramOptionsQuery();

  const { data: partnerOptionsData, loading: partnerOptionsLoading } =
    usePlmPartnerOptionsQuery();

  const { data: userOptionsData, loading: userOptionsLoading } =
    usePlmContractUserOptionsQuery();

  const { properties: propertyOptionsData, loading: propertyOptionsLoading } =
    useProperties();
  // const { data: propertyOptionsData, loading: propertyOptionsLoading } =

  const initialValues = calculatePlmContractFormValues(plmContract);

  const onSubmit = async (values: FormValues) => {
    try {
      const meterIds =
        values.scopeType === EnumPlmContractScopeType.Property
          ? []
          : values.meterIds.map((id) => id.trim()).filter(Boolean);
      const plmContractPayload = {
        ...values,
        meterIds,
        dispatcherIds: values.dispatcherIds as any,
      };

      if (plmContract?._id) {
        if (!can('update', { __typename: 'PlmContract' })) {
          Notifier.error('You do not have permission to update PLM contracts');
          return;
        }

        await updatePlmContract({
          variables: {
            id: plmContract._id,
            plmContract: plmContractPayload,
          },
        });

        Notifier.success('PLM contract updated successfully');
      } else {
        if (!can('create', { __typename: 'PlmContract' })) {
          Notifier.error('You do not have permission to create PLM contracts');
          return;
        }

        const result = await createPlmContract({
          variables: {
            plmContract: plmContractPayload,
          },
        });

        if (result.data?.createPlmContract?._id) {
          Notifier.success('PLM contract created successfully');
          navigate(`/plmContracts`);
        } else {
          Notifier.error('PLM contract creation failed');
        }
      }
    } catch (error) {
      Notifier.error('Error saving PLM contract');
      console.error(error);
    }
  };

  if (
    loading ||
    programOptionsLoading ||
    partnerOptionsLoading ||
    userOptionsLoading ||
    propertyOptionsLoading
  ) {
    return (
      <Box
        component="div"
        display="flex"
        flexDirection="column"
        justifyContent="center"
        alignItems="center"
        width="100%"
        height="100%"
      >
        <Box sx={{ marginBottom: '2rem' }}>
          <CircularProgress color="secondary" size="3rem" />
        </Box>
        <Box>
          <Typography variant="h4" color="secondary">
            Preparing your data...
          </Typography>
        </Box>
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="200px"
        sx={{ padding: '16px' }}
      >
        <Typography color="error">
          Error loading PLM contract: {error.message}
        </Typography>
      </Box>
    );
  }

  const programOptions = programOptionsData?.plmPrograms || [];
  const propertyOptions = propertyOptionsData || [];

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={onSubmit}
    >
      {({
        errors,
        touched,
        handleChange,
        handleBlur,
        values,
        isSubmitting,
        setFieldValue,
      }) => (
        <Form style={{ padding: '10px' }}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <BorderedSection title="Contract Information">
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      name="name"
                      label="Name"
                      value={values.name}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={touched.name && Boolean(errors.name)}
                      helperText={touched.name && errors.name}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Box display="flex" gap={1} alignItems="flex-start">
                      <TextField
                        fullWidth
                        name="contractId"
                        label="Contract ID"
                        value={values.contractId}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={touched.contractId && Boolean(errors.contractId)}
                        helperText={touched.contractId && errors.contractId}
                      />
                      {!plmContract?._id && (
                        <Tooltip title="Generate UUID for Contract ID">
                          <span>
                            <IconButton
                              onClick={async () => {
                                await setFieldValue('contractId', uuIdv4());
                                Notifier.success('Contract ID generated');
                              }}
                              sx={{ mt: 1 }}
                            >
                              <Refresh />
                            </IconButton>
                          </span>
                        </Tooltip>
                      )}
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      select
                      name="programId"
                      label="Program"
                      value={values.programId}
                      onChange={async (e) => {
                        const selectedProgramId = e.target.value;
                        handleChange(e);

                        // Set the name field to the selected program's name if name is empty
                        if (selectedProgramId && !values.name) {
                          const selectedProgram = programOptions.find(
                            (program) => program._id === selectedProgramId,
                          );
                          if (selectedProgram) {
                            await setFieldValue('name', selectedProgram.name);
                          }
                        }
                      }}
                      onBlur={handleBlur}
                      error={touched.programId && Boolean(errors.programId)}
                      helperText={touched.programId && errors.programId}
                    >
                      <MenuItem value="">
                        <em>Select a program</em>
                      </MenuItem>
                      {programOptions.map((program) => (
                        <MenuItem key={program._id} value={program._id}>
                          {program.name} (
                          {program.partner?.name || 'Unknown partner'})
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      select
                      name="propertyIds"
                      label="Properties"
                      value={values.propertyIds}
                      onChange={async (e) => {
                        const value = e.target.value;
                        await setFieldValue(
                          'propertyIds',
                          value?.split ? value.split(',') : value,
                        );
                      }}
                      onBlur={handleBlur}
                      error={touched.propertyIds && Boolean(errors.propertyIds)}
                      helperText={touched.propertyIds && errors.propertyIds}
                      SelectProps={{
                        multiple: true,
                        renderValue: (selected) => {
                          const selectedIds = selected as string[];
                          const selectedProperties = propertyOptions.filter(
                            (prop) => selectedIds.includes(prop._id),
                          );
                          return selectedProperties
                            .map((prop) => prop.title)
                            .join(', ');
                        },
                      }}
                    >
                      {propertyOptions.map((property) => (
                        <MenuItem key={property._id} value={property._id}>
                          {property.title}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      select
                      name="scopeType"
                      label="Participation scope"
                      value={values.scopeType}
                      onChange={async (e) => {
                        handleChange(e);
                        if (
                          e.target.value === EnumPlmContractScopeType.Property
                        ) {
                          await setFieldValue('meterIds', []);
                        }
                      }}
                      onBlur={handleBlur}
                      error={touched.scopeType && Boolean(errors.scopeType)}
                      helperText={touched.scopeType && errors.scopeType}
                    >
                      <MenuItem value={EnumPlmContractScopeType.Property}>
                        Property (all meters on selected properties)
                      </MenuItem>
                      <MenuItem value={EnumPlmContractScopeType.Meter}>
                        Specific meters only
                      </MenuItem>
                    </TextField>
                  </Grid>
                  {values.scopeType === EnumPlmContractScopeType.Meter && (
                    <Grid item xs={12}>
                      <Autocomplete
                        multiple
                        freeSolo
                        options={[] as string[]}
                        value={values.meterIds}
                        onChange={(_, v) => setFieldValue('meterIds', v)}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Meter / device IDs"
                            error={touched.meterIds && Boolean(errors.meterIds)}
                            helperText={
                              (touched.meterIds && errors.meterIds) as string
                            }
                          />
                        )}
                      />
                    </Grid>
                  )}
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      select
                      name="partnerId"
                      label="Partner"
                      value={values.partnerId}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={touched.partnerId && Boolean(errors.partnerId)}
                      helperText={touched.partnerId && errors.partnerId}
                    >
                      <MenuItem value="">
                        <em>Select a partner</em>
                      </MenuItem>
                      {partnerOptionsData?.plmPartners?.map((partner) => (
                        <MenuItem key={partner._id} value={partner._id}>
                          {partner.name}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      select
                      name="dispatcherIds"
                      label="Dispatchers"
                      value={values.dispatcherIds}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={
                        touched.dispatcherIds && Boolean(errors.dispatcherIds)
                      }
                      helperText={touched.dispatcherIds && errors.dispatcherIds}
                      SelectProps={{
                        multiple: true,
                        renderValue: (selected) => {
                          const selectedIds = selected as string[];
                          const selectedUsers = userOptionsData?.users?.filter(
                            (user) => selectedIds.includes(user._id),
                          );
                          return (
                            selectedUsers
                              ?.map((user) => user.name)
                              .join(', ') || ''
                          );
                        },
                      }}
                    >
                      {userOptionsData?.users
                        ?.filter((user) => {
                          // Show super users regardless of property selection
                          if (user.authorizationProfile?.isSuper) {
                            return true;
                          }

                          // Show users who are managers for any of the selected properties
                          if (values.propertyIds.length > 0) {
                            const userManagerProperties =
                              user.authorizationProfile?.managerProperties ||
                              [];
                            return values.propertyIds.some((propertyId) =>
                              userManagerProperties.includes(propertyId),
                            );
                          }

                          // If no properties are selected, only show super users
                          return false;
                        })
                        .map((user) => (
                          <MenuItem key={user._id} value={user._id}>
                            {user.name} ({user.email})
                          </MenuItem>
                        ))}
                    </TextField>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      name="minimumReductionKW"
                      label="Minimum Reduction (kW)"
                      type="number"
                      value={values.minimumReductionKW}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={
                        touched.minimumReductionKW &&
                        Boolean(errors.minimumReductionKW)
                      }
                      helperText={
                        touched.minimumReductionKW && errors.minimumReductionKW
                      }
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      name="maximumEventsPerSeason"
                      label="Maximum Events per Season"
                      type="number"
                      value={values.maximumEventsPerSeason}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={
                        touched.maximumEventsPerSeason &&
                        Boolean(errors.maximumEventsPerSeason)
                      }
                      helperText={
                        touched.maximumEventsPerSeason &&
                        errors.maximumEventsPerSeason
                      }
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      select
                      name="baselineMethod"
                      label="Baseline Method"
                      value={values.baselineMethod}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={
                        touched.baselineMethod && Boolean(errors.baselineMethod)
                      }
                      helperText={
                        touched.baselineMethod && errors.baselineMethod
                      }
                    >
                      {Object.values(EnumPlmContractBaselineMethod).map(
                        (option) => (
                          <MenuItem key={option} value={option}>
                            {option}
                          </MenuItem>
                        ),
                      )}
                    </TextField>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Box display="flex" gap={1} alignItems="flex-start">
                      <TextField
                        fullWidth
                        name="incentiveDescription"
                        label="Incentive"
                        value={values.incentiveDescription || ''}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={
                          touched.incentiveDescription &&
                          Boolean(errors.incentiveDescription)
                        }
                        helperText={
                          touched.incentiveDescription &&
                          errors.incentiveDescription
                        }
                      />
                      <Tooltip title="Copy incentive from selected program">
                        <span>
                          <IconButton
                            onClick={async () => {
                              if (values.programId) {
                                const selectedProgram = programOptions.find(
                                  (program) => program._id === values.programId,
                                );
                                if (selectedProgram?.incentiveDescription) {
                                  await setFieldValue(
                                    'incentiveDescription',
                                    selectedProgram.incentiveDescription,
                                  );
                                  Notifier.success(
                                    'Incentive copied from program',
                                  );
                                } else {
                                  Notifier.info(
                                    'Selected program has no incentive description',
                                  );
                                }
                              }
                            }}
                            disabled={!values.programId}
                            sx={{ mt: 1 }}
                          >
                            <ContentCopy />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      name="penaltyForNonCompliance"
                      label="Penalty for Non-Compliance"
                      type="number"
                      value={values.penaltyForNonCompliance || ''}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={
                        touched.penaltyForNonCompliance &&
                        Boolean(errors.penaltyForNonCompliance)
                      }
                      helperText={
                        touched.penaltyForNonCompliance &&
                        errors.penaltyForNonCompliance
                      }
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      select
                      name="status"
                      label="Status"
                      value={values.status}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={touched.status && Boolean(errors.status)}
                      helperText={touched.status && errors.status}
                    >
                      {Object.values(EnumPlmContractStatus).map((option) => (
                        <MenuItem key={option} value={option}>
                          {option}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                  <Grid item xs={12}>
                    <Box display="flex" gap={2} alignItems="flex-start">
                      <TextField
                        fullWidth
                        name="notificationEmail"
                        label="Notification Email"
                        value={values.notificationEmail}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={
                          touched.notificationEmail &&
                          Boolean(errors.notificationEmail)
                        }
                        helperText={
                          touched.notificationEmail && errors.notificationEmail
                        }
                      />
                      <Button
                        variant="outlined"
                        onClick={async () => {
                          await setFieldValue(
                            'notificationEmail',
                            `${uuIdv4()}@plm.embue.com`,
                          );
                        }}
                        sx={{ minWidth: '120px', height: '56px' }}
                      >
                        Regenerate
                      </Button>
                    </Box>
                  </Grid>
                </Grid>
              </BorderedSection>
            </Grid>

            <Grid item xs={12}>
              <Box display="flex" justifyContent="flex-end" gap={2}>
                <Button variant="outlined" onClick={() => navigate(-1)}>
                  Cancel
                </Button>
                <Button
                  startIcon={<SaveIcon />}
                  type="submit"
                  variant="contained"
                  color="primary"
                  disabled={isSubmitting}
                >
                  {plmContract?._id ? 'Update' : 'Create'}
                </Button>
              </Box>
            </Grid>
          </Grid>
        </Form>
      )}
    </Formik>
  );
};
